import { Monitor, Moon, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { type ThemePreference } from "../lib/theme-context";
import { useTheme } from "../lib/use-theme";
import { cn } from "../lib/utils";

/**
 * Topbar theme switcher (#161). Three-way picker - System / Light /
 * Dark. Closed state shows the currently-resolved icon; open shows
 * the three options as a vertical menu.
 */
export function ThemePicker() {
  const { t } = useTranslation();
  const { preference, resolved, setPreference } = useTheme();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Click-outside collapses.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && e.target instanceof Node && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const TriggerIcon = resolved === "dark" ? Moon : Sun;

  const options: ReadonlyArray<{ value: ThemePreference; icon: typeof Sun; labelKey: string }> = [
    { value: "system", icon: Monitor, labelKey: "theme.system" },
    { value: "light", icon: Sun, labelKey: "theme.light" },
    { value: "dark", icon: Moon, labelKey: "theme.dark" },
  ];

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={t("theme.title")}
        aria-label={t("theme.title")}
        className="flex h-9 w-9 items-center justify-center rounded-full text-muted hover:bg-surface-strong hover:text-strong"
      >
        <TriggerIcon className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label={t("theme.title")}
          className="absolute right-0 z-30 mt-1 w-36 rounded-md border border-line bg-surface py-1 shadow-lg"
        >
          {options.map((opt) => {
            const Icon = opt.icon;
            const active = preference === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setPreference(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm",
                  active
                    ? "bg-surface-strong text-strong"
                    : "text-foreground hover:bg-surface-muted",
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{t(opt.labelKey)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
